/**
 * 감사장 프리뷰 렌더 — web의 renderThankYouHtml과 동일 계약의 admin 로컬 구현.
 * (admin은 web 코드를 import할 수 없는 독립 SPA)
 */
import { Liquid } from 'liquidjs'

const engine = new Liquid({ strictVariables: false, strictFilters: false, lenientIf: true })

engine.registerFilter('nl2br', (v: unknown) => {
  if (typeof v !== 'string') return ''
  return v.replace(/\n/g, '<br>')
})

/** 날짜 포맷 — web `lib/templateEngine.ts`의 formatDate 미러 (KST 벽시계 고정) */
engine.registerFilter('formatDate', (v: unknown, format?: string) => {
  if (!v) return ''
  const parsed = new Date(String(v))
  if (isNaN(parsed.getTime())) return String(v)
  const d = new Date(parsed.getTime() + 9 * 60 * 60 * 1000)

  if (format === 'korean') {
    return `${d.getUTCFullYear()}년 ${d.getUTCMonth() + 1}월 ${d.getUTCDate()}일`
  }
  if (format === 'time') {
    const h = d.getUTCHours()
    const m = d.getUTCMinutes()
    const period = h < 12 ? '오전' : '오후'
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${period} ${hour12}시${m > 0 ? ` ${m}분` : ''}`
  }
  if (format === 'day') {
    const days = ['일', '월', '화', '수', '목', '금', '토']
    return days[d.getUTCDay()] + '요일'
  }

  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')

  // 커스텀 패턴: YYYY/YY/MM/M/DD/D/ddd 토큰 치환 (예: 'MM-DD' → 10-10, 'M월 D일' → 10월 10일)
  if (format && /[YMD]|ddd/.test(format)) {
    const days = ['일', '월', '화', '수', '목', '금', '토']
    return format
      .replace(/ddd/g, days[d.getUTCDay()]) // 요일 먼저 — 치환 결과(한글)는 이후 토큰과 충돌 없음
      .replace(/YYYY/g, String(d.getUTCFullYear()))
      .replace(/YY/g, String(d.getUTCFullYear()).slice(-2))
      .replace(/MM/g, mm)
      .replace(/M/g, String(d.getUTCMonth() + 1))
      .replace(/DD/g, dd)
      .replace(/D/g, String(d.getUTCDate()))
  }

  // 기본: YYYY.MM.DD
  return `${d.getUTCFullYear()}.${mm}.${dd}`
})

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * 디자인 HTML에서 가장 큰 photoN 번호를 사진 슬롯 개수로 산출. 없으면 0.
 * (web `@fiket/shared`의 countPhotoSlots 미러 — admin은 독립 SPA라 복제)
 */
export function countPhotoSlots(html: string): number {
  let max = 0
  for (const m of html.matchAll(/\bphoto(\d+)\b/g)) {
    const n = Number(m[1])
    if (n > max) max = n
  }
  return max
}

/** 프리뷰용 샘플 카드 데이터 */
export const SAMPLE_CARD_DATA = {
  // 비워둬 디자인 HTML의 {{ message | default: "..." }} 기본 문구가 렌더되게 한다
  // (실제 새 카드도 빈 message로 시작 → 디자인 default가 발동하므로 미리보기와 결과 일치).
  message: '',
  name1: '홍길동',
  name2: '김지연',
  names: '홍길동 · 김지연 올림',
  photoUrl: '/thankyou-letter/images/sample.jpg',
  weddingDate: '2026-10-10',
}

export async function renderThankYouPreview(html: string, data: typeof SAMPLE_CARD_DATA = SAMPLE_CARD_DATA): Promise<string> {
  // 디자인이 요구하는 슬롯 수만큼 단일 샘플 이미지를 반복 주입해 모든 슬롯이 채워진 프리뷰를 보장
  const slotCount = countPhotoSlots(html)
  const sampleUrl = data.photoUrl ? escapeHtml(data.photoUrl) : undefined
  const scope: Record<string, unknown> = {
    message: escapeHtml(data.message),
    // name1/name2 개별 주입 + names는 합성값(구 디자인 호환) — web 렌더러와 동일 계약
    name1: data.name1 ? escapeHtml(data.name1) : undefined,
    name2: data.name2 ? escapeHtml(data.name2) : undefined,
    names: escapeHtml(data.names),
    // raw(YYYY-MM-DD)로 주입 — 표시 포맷은 디자인이 Liquid 필터로 선택 (web 렌더러와 동일 계약)
    weddingDate: data.weddingDate ? escapeHtml(data.weddingDate) : undefined,
    photoUrl: sampleUrl, // photo1 별칭(구 디자인 호환)
  }
  for (let i = 1; i <= slotCount; i++) {
    scope[`photo${i}`] = sampleUrl
  }
  return engine.parseAndRender(html, scope)
}
